import { useTranslation } from "react-i18next";
import type { PrizeDrop } from "@/campaigns/modules/prizedrop/types";
import { CampaignFinishedCard } from "@/components/campaign-card/campaign-finished-card";
import { CampaignJoinState } from "@/components/campaign-card/campaign-join-cta";
import { CampaignDockPanel } from "@/components/campaigns/campaign-shell/campaign-dock-panel";
import { FINISHED_DIM } from "@/components/campaigns/campaign-shell/campaign-view";
import { cn } from "@/lib/utils";
import { PrizeDropRevealCard } from "./prizedrop-reveal-card";
import { PrizeDropList, RemainingPrizeDrops } from "./prizedrop-sections";

/**
 * A prize drop in the overlay's dock column: the detail view's sections at
 * dock density — a won prize waiting to be revealed first, then how many
 * prizes are left with the join state under the figure, then every tier the
 * player can win. The tiers name their prizes because the pool is public; only
 * a won prize stays hidden until its reveal (#3922). Once it is over, the
 * detail view's finished card follows the reveal and the stats grey out as
 * history — the reveal never does: a win on a finished drop is still owed.
 */
export const PrizeDropDockPanel = ({
  prizeDrop,
  joined,
}: {
  prizeDrop: PrizeDrop;
  joined: boolean;
}) => {
  const { t } = useTranslation();
  const finished = prizeDrop.campaign.status === "FI";
  const joinState = (
    <CampaignJoinState campaign={prizeDrop.campaign} joined={joined} />
  );

  return (
    <CampaignDockPanel minimumBet={prizeDrop.minimum_bet}>
      {/* The same reveal as the detail view and the in-game button; the dock's
          is the one mount, so it owns the (portalled) reveal window. */}
      <PrizeDropRevealCard prizeDrop={prizeDrop} ownsWindowOn="always" />
      {finished && (
        <CampaignFinishedCard
          campaign={prizeDrop.campaign}
          title={t("campaign.prizeDropsFinished")}
        />
      )}
      <div className={cn("flex flex-col gap-3", finished && FINISHED_DIM)}>
        {/* Until the tiers' first push lands there is no count to show, only
            the join state — not a "0" that reads as "all dropped". */}
        {prizeDrop.prizes.length > 0 ? (
          <RemainingPrizeDrops prizeDrop={prizeDrop} density="dock">
            {joinState}
          </RemainingPrizeDrops>
        ) : (
          joinState
        )}
        <PrizeDropList prizeDrop={prizeDrop} />
      </div>
    </CampaignDockPanel>
  );
};
